class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._akDesiredOpen = false;
    this._akVisibilityVersion = 0;
    this.setAttribute('aria-hidden', 'true');
    this._akViewport = this.syncViewport.bind(this);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.classList.contains('active')) { event.preventDefault(); this.close(); }
    });
    this.addEventListener('click', event => {
      if (event.target.closest('[data-ak-cart-close],#CartDrawer-Overlay')) { event.preventDefault(); this.close(); }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', event => { event.preventDefault(); this.open(cartLink); });
    cartLink.addEventListener('keydown', event => {
      if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this.open(cartLink); }
    });
  }

  syncViewport() {
    if (!this.classList.contains('active')) return;
    const viewport = window.visualViewport;
    const top = viewport ? viewport.offsetTop : 0;
    let height = viewport ? viewport.height : window.innerHeight;
    const preview = document.getElementById('PBarNextFrame');
    if (preview) {
      const rect = preview.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom >= top + height - 2) {
        if (rect.height < height * .35 && rect.top > top) height = Math.min(height, rect.top - top);
        else if (rect.top <= top + 2 && rect.height >= height * .9) height -= 64;
      }
    }
    this.style.setProperty('--ak-cart-vh', Math.max(180, Math.floor(height)) + 'px');
    this.style.setProperty('--ak-cart-top', Math.max(0, Math.floor(top)) + 'px');
  }

  open(triggeredBy) {
    const alreadyOpen = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this._akDesiredOpen = true;
    this._akVisibilityVersion++;
    clearTimeout(this._akRenderTimer);
    this.classList.add('animate', 'active');
    this.setAttribute('aria-hidden', 'false');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('ak-cart-open');
    window.removeEventListener('resize', this._akViewport);
    window.addEventListener('resize', this._akViewport, { passive: true });
    window.visualViewport?.removeEventListener('resize', this._akViewport);
    window.visualViewport?.removeEventListener('scroll', this._akViewport);
    window.visualViewport?.addEventListener('resize', this._akViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', this._akViewport, { passive: true });
    this.syncViewport();
    if (!alreadyOpen) {
      const scroll = this.querySelector('.ak-cart-scroll');
      if (scroll) scroll.scrollTop = 0;
    }
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    cancelAnimationFrame(this._akFocusFrame);
    this._akFocusFrame = requestAnimationFrame(() => {
      if (!this._akDesiredOpen || !this.classList.contains('active')) return;
      const panel = this.querySelector('.drawer__inner');
      const close = this.querySelector('.drawer__header [data-ak-cart-close]');
      if (panel && typeof trapFocus === 'function') trapFocus(panel, close || panel);
      close?.focus({ preventScroll: true });
    });
  }

  close() {
    this._akDesiredOpen = false;
    this._akVisibilityVersion++;
    clearTimeout(this._akRenderTimer);
    cancelAnimationFrame(this._akFocusFrame);
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    this.activeElement?.focus?.({ preventScroll: true });
    this.setAttribute('aria-hidden', 'true');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('ak-cart-open');
    window.removeEventListener('resize', this._akViewport);
    window.visualViewport?.removeEventListener('resize', this._akViewport);
    window.visualViewport?.removeEventListener('scroll', this._akViewport);
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentElement.hasAttribute('open') ? 'true' : 'false');
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.parentElement.addEventListener('toggle', () => summary.setAttribute('aria-expanded', summary.parentElement.hasAttribute('open') ? 'true' : 'false'));
  }

  renderContents(parsedState, options = {}) {
    const scrollTop = this.querySelector('.ak-cart-scroll')?.scrollTop || 0;
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach(section => {
      const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!element || typeof html !== 'string') return;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
      if (next) element.innerHTML = next.innerHTML;
    });
    this.classList.toggle('is-empty', !!this.querySelector('cart-drawer-items.is-empty'));
    if (options.preserveScroll) {
      const scroll = this.querySelector('.ak-cart-scroll');
      if (scroll) scroll.scrollTop = scrollTop;
    }
    const version = this._akVisibilityVersion;
    clearTimeout(this._akRenderTimer);
    this._akRenderTimer = setTimeout(() => {
      if (version === this._akVisibilityVersion && options.open !== false) this.open();
    }, 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }
  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }
  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onChange(event) {
    if (event.target.type !== 'number') return;
    const quantity = Number(event.target.value);
    if (!Number.isInteger(quantity) || quantity < 0) return;
    this.updateQuantity(Number(event.target.dataset.index), quantity);
  }
  async onCartUpdate() {
    try {
      const response = await fetch(routes.cart_url + '?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();
      const drawer = document.querySelector('cart-drawer');
      const wasOpen = drawer.classList.contains('active');
      drawer.renderContents({ sections }, { open: wasOpen && drawer._akDesiredOpen !== false, preserveScroll: true });
    } catch(error) { console.error(error); }
  }
  async updateQuantity(line, quantity) {
    if (window.akCartBusy) return;
    window.akCartBusy = true;
    const drawer = document.querySelector('cart-drawer');
    const errorElement = drawer.querySelector('#CartDrawer-CartErrors');
    const lockedControls = [...drawer.querySelectorAll('input,button')].filter(el => !el.disabled && !el.hasAttribute('data-ak-cart-close'));
    lockedControls.forEach(el => el.disabled = true);
    try {
      const response = await fetch(routes.cart_change_url, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({line:Number(line),quantity:Number(quantity),sections:['cart-drawer','cart-icon-bubble'],sections_url:location.pathname.startsWith('/')?location.pathname:'/cart'})});
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error(data.description || data.errors || 'No pudimos actualizar el carrito.');
      drawer.classList.toggle('is-empty',data.item_count===0);
      drawer.renderContents(data, { open: drawer._akDesiredOpen !== false, preserveScroll: true });
      if (typeof publish==='function') publish(PUB_SUB_EVENTS.cartUpdate,{source:'cart-drawer-items',cartData:data});
    } catch(error) {
      await this.onCartUpdate();
      const currentError = drawer.querySelector('#CartDrawer-CartErrors');
      if (currentError) currentError.textContent=error.message;
      if (typeof publish==='function') publish(PUB_SUB_EVENTS.cartUpdate,{source:'cart-drawer-items'});
    } finally {
      window.akCartBusy = false;
      lockedControls.filter(el => el.isConnected).forEach(el => el.disabled = false);
      const checkout=drawer.querySelector('[name=checkout]');
      const minimum=drawer.querySelector('[data-ak-minimum]');
      if(checkout && (drawer.classList.contains('is-empty') || (minimum && Number(minimum.dataset.total)<10000000))) checkout.disabled=true;
    }
  }
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


(function(){
const cache=new Map();let version=0,lastLevel=null;
const money=n=>new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(n/100);
const norm=s=>(s||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
const root=window.Shopify?.routes?.root||'/';
function rewards(){
 const el=document.querySelector('[data-ak-rewards]');if(!el)return;
 const base=Number(el.dataset.base),total=Number(el.dataset.total),saving=Number(el.dataset.saving);
 const shipping=total>=20000000;
 const level=base>=100000100?3:base>=50000100?2:shipping?1:0;
 const target=!shipping?20000000:base<50000100?50000100:base<100000100?100000100:0;
 const gap=target?Math.max(0,target-(!shipping?total:base)):0;
 const label=!shipping?'el envío gratis':base<50000100?'10% de descuento':'20% de descuento';
 const progress=base>=100000100?100:base>=50000100?50+(base-50000100)/50000000*50:shipping?(base-20000000)/30000100*50:0;
 el.querySelector('[data-ak-title]').textContent=!gap?'¡Alcanzaste todas las metas!':gap<=target*.25?'¡Tu próximo premio está cerca!':'Tu próximo premio te espera';
 el.querySelector('[data-ak-message]').textContent=gap?'Agrega '+money(gap).replace(/\s/g,'')+' en productos elegibles para completar '+label+'.':'Disfruta los beneficios de tu compra.';
 el.querySelector('[data-ak-progress]').value=Math.max(0,Math.min(100,progress));
 el.querySelector('[data-ak-track]').style.width=Math.max(0,Math.min(100,progress))+'%';
 el.querySelectorAll('[data-ak-step]').forEach(step=>{
 const n=Number(step.dataset.akStep),complete=n===1?shipping:base>=(n===2?50000100:100000100);
 step.dataset.state=complete?'complete':n===level+1?'current':'locked';
 step.setAttribute('aria-label',step.textContent.trim()+(complete?' · Meta alcanzada':n===level+1?' · Próxima meta':' · Pendiente'));
 if(n===level+1)step.setAttribute('aria-current','step');else step.removeAttribute('aria-current');
 });
 el.querySelector('[data-ak-shipping]').hidden=!shipping;
 const savings=el.querySelector('[data-ak-saving]');savings.hidden=!saving;savings.textContent=saving?'Tu ahorro aplicado: '+money(saving):'';
 if(lastLevel!==null&&level>lastLevel&&!matchMedia('(prefers-reduced-motion: reduce)').matches)el.animate([{opacity:.65},{opacity:1}],{duration:350});
 lastLevel=level;
}
async function candidates(id){
 if(cache.has(id))return cache.get(id);
 const promise=(async()=>{const ctrl=new AbortController(),timer=setTimeout(()=>ctrl.abort(),4500);
 try{const r=await fetch(root+'recommendations/products.json?product_id='+id+'&limit=8&intent=related',{signal:ctrl.signal});if(!r.ok)return [];return (await r.json()).products||[];}catch{return [];}finally{clearTimeout(timer);}
 })();cache.set(id,promise);if(cache.size>30)cache.delete(cache.keys().next().value);return promise;
}
function setupCarousel(section){
 const rail=section.querySelector('.ak-cart-recs'),cards=[...rail.children],dots=section.querySelector('[data-ak-rec-dots]');
 if(!dots)return;
 dots.replaceChildren();
 function go(index){const card=cards[Math.max(0,Math.min(cards.length-1,index))];rail.scrollTo({left:card.offsetLeft-cards[0].offsetLeft,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});}
 cards.forEach((card,i)=>{card.setAttribute('aria-label','Producto '+(i+1)+' de '+cards.length);const b=document.createElement('button');b.type='button';b.setAttribute('aria-label','Ver producto '+(i+1));b.addEventListener('click',()=>go(i));dots.append(b);});
 const prev=section.querySelector('[data-ak-rec-prev]'),next=section.querySelector('[data-ak-rec-next]');
 let active=0;
 function update(){let distance=Infinity;cards.forEach((card,i)=>{const d=Math.abs(card.offsetLeft-cards[0].offsetLeft-rail.scrollLeft);if(d<distance){distance=d;active=i;}});
 [...dots.children].forEach((b,i)=>{b.setAttribute('aria-current',i===active?'true':'false');});
 prev.disabled=rail.scrollLeft<=2;next.disabled=rail.scrollLeft>=rail.scrollWidth-rail.clientWidth-2;
 }
 prev.onclick=()=>go(active-1);next.onclick=()=>go(active+1);
 rail.addEventListener('scroll',update,{passive:true});
 section.addEventListener('toggle',()=>{if(section.open)requestAnimationFrame(update);});
 rail.addEventListener('keydown',e=>{if(e.key==='ArrowRight'){e.preventDefault();go(active+1);}if(e.key==='ArrowLeft'){e.preventDefault();go(active-1);}});
 prev.hidden=next.hidden=dots.hidden=cards.length<2;
 requestAnimationFrame(update);
}
async function render(){
 rewards();const section=document.querySelector('[data-ak-related]');if(!section)return;
 const token=++version;let items;try{items=JSON.parse(section.querySelector('[data-ak-cart-items]').textContent);}catch{return;}
 const ids=new Set(items.map(i=>i.id));const seeds=[...new Map(items.map(i=>[i.id,i])).values()].slice(0,3);
 const groups=await Promise.all(seeds.map(i=>candidates(i.id)));if(token!==version||!section.isConnected)return;
 const seen=new Set(),list=[];
 groups.forEach((group,index)=>group.forEach(p=>{
 if(ids.has(p.id)||seen.has(p.id)||!p.available)return;
 const source=seeds[index],type=norm(p.type),words=norm(source.title).split(/\W+/).filter(w=>w.length>4&&!['remax','aleko','intex','negro','blanco','precio','mayorista'].includes(w));
 const score=(type&&type===norm(source.type)?10:0)+words.filter(w=>norm(p.title).includes(w)).length*3;
 if(score<3)return;seen.add(p.id);list.push({p,score});
 }));
 list.sort((a,b)=>b.score-a.score);const rail=section.querySelector('.ak-cart-recs');rail.replaceChildren();
 list.slice(0,4).forEach(({p})=>{
 const card=document.createElement('article');card.className='ak-cart-rec';
 const a=document.createElement('a');a.href=p.url;
 if(p.featured_image){const img=document.createElement('img');const url=new URL(p.featured_image,document.baseURI);url.searchParams.set('width','540');img.src=url.href;img.loading='lazy';img.decoding='async';img.width=270;img.height=180;img.alt=p.title;a.append(img);}
 const title=document.createElement('span');title.textContent=p.title;a.append(title);card.append(a);
 const price=document.createElement('strong');price.textContent=money(p.price);card.append(price);
 if(p.variants?.length===1&&p.variants[0].available){const b=document.createElement('button');b.type='button';b.textContent='+ Agregar';b.dataset.cartRecAdd=p.variants[0].id;card.append(b);}
 else{const a=document.createElement('a');a.href=p.url;a.className='ak-cart-rec-options';a.textContent='Elegir opciones';card.append(a);}
 rail.append(card);
 });section.hidden=!rail.children.length;
 if(!section.hidden) setupCarousel(section);
}
const drawer=document.querySelector('cart-drawer');if(!drawer)return;
const original=drawer.renderContents.bind(drawer);drawer.renderContents=function(data,options){original(data,options);render();};render();
document.addEventListener('click',async e=>{
 const button=e.target.closest('[data-cart-rec-add]');if(!button||button.disabled||window.akCartBusy)return;
 window.akCartBusy=true;button.disabled=true;
 try{const r=await fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:Number(button.dataset.cartRecAdd),quantity:1}],sections:['cart-drawer','cart-icon-bubble'],sections_url:location.pathname.startsWith('/')?location.pathname:'/cart'})});
 const data=await r.json();if(!r.ok)throw Error(data.description||'No pudimos agregar el producto.');drawer.renderContents(data,{open:drawer._akDesiredOpen!==false,preserveScroll:true});
 }catch(error){const box=document.querySelector('.ak-cart-rec-error');if(box)box.textContent=error.message;}
 finally{window.akCartBusy=false;button.disabled=false;}
});
})();

